/* Accessibility repairs for Ghost Members forms and the same-origin Portal iframe.
 * Keep Ghost's event handlers, validation and consent state in charge of submission.
 */
(function () {
    'use strict';

    var frame = null;
    var portalDoc = null;
    var portalObserver = null;
    var previousFocus = null;
    var inertElements = [];
    var pending = null;
    var hookedFrames = new WeakSet();
    var hookedForms = new WeakSet();
    var validationRequested = false;

    var PORTAL_CSS = [
        '.gh-portal-signup-terms input[type="checkbox"] { display:block !important; position:absolute; width:1px; height:1px; opacity:0; overflow:hidden; }',
        '.gh-portal-signup-terms input:focus-visible + .checkbox, .gh-portal-input:focus-visible, .gh-portal-products button:focus-visible { outline:3px solid #1d6d96 !important; outline-offset:3px; }',
        '.gh-portal-popup-container:is(.signin,.signup) .gh-portal-btn-primary { background-color:#1d6d96 !important; color:#fff !important; }',
        '.gh-portal-popup-container:is(.signin,.signup) .gh-portal-btn-link { color:#1d6d96 !important; }',
        '[data-yoby-auth-live] { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip-path:inset(50%); white-space:nowrap; }',
        'html.yoby-a11y-text-lg { font-size:66.25%; }',
        'html.yoby-a11y-text-xl { font-size:73.75%; }',
        'html.yoby-a11y-spacing :is(p,label,button,input) { letter-spacing:.03em !important; line-height:1.65 !important; }',
        'html.yoby-a11y-readable-font :is(body,h1,h2,h3,p,button,input,label) { font-family:system-ui,sans-serif !important; }',
        'html.yoby-a11y-underline-links a { text-decoration:underline !important; }',
        'html.yoby-a11y-focus-strong :focus-visible { outline:3px solid #f59e0b !important; outline-offset:3px !important; }',
        'html.yoby-a11y-reduce-motion *, html.yoby-a11y-reduce-motion *::before, html.yoby-a11y-reduce-motion *::after { animation-duration:.01ms !important; animation-iteration-count:1 !important; transition-duration:.01ms !important; scroll-behavior:auto !important; }'
    ].join('\n');

    function text(key, fallback) {
        if (!window.YOBY_i18n) return fallback;
        var result = window.YOBY_i18n.t('auth.a11y.' + key);
        return result === 'auth.a11y.' + key ? fallback : result;
    }

    function attr(el, name, value) {
        if (el && el.getAttribute(name) !== value) el.setAttribute(name, value);
    }

    function describe(input, id, on) {
        var ids = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
        ids = ids.filter(function (value) { return value !== id; });
        if (on) ids.push(id);
        if (ids.length) attr(input, 'aria-describedby', ids.join(' '));
        else input.removeAttribute('aria-describedby');
    }

    function visible(el) {
        var view = el.ownerDocument.defaultView;
        return !el.closest('[hidden], [inert], .hidden') && view.getComputedStyle(el).display !== 'none' && view.getComputedStyle(el).visibility !== 'hidden' && el.getClientRects().length > 0;
    }

    function focusables(root) {
        return Array.prototype.slice.call(root.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')).filter(function (el) {
            return !el.disabled && el.tabIndex >= 0 && visible(el);
        });
    }

    function portalKeydown(e) {
        var dialog = portalDoc && portalDoc.querySelector('.gh-portal-popup-wrapper');
        if (!dialog) return;
        if (e.key === 'Enter' && e.target.matches('.gh-portal-input')) validationRequested = true;
        if (e.key === 'Escape') {
            var close = dialog.querySelector('[data-test-button="close-popup"]:not([disabled])');
            if (close) {
                e.preventDefault();
                e.stopImmediatePropagation();
                close.click();
            }
            return;
        }
        if (e.key !== 'Tab' || e.ctrlKey || e.altKey || e.metaKey) return;
        var items = focusables(dialog);
        if (!items.length) return;
        var index = items.indexOf(portalDoc.activeElement);
        var next = index < 0
            ? (e.shiftKey ? items[items.length - 1] : items[0])
            : items[(index + (e.shiftKey ? -1 : 1) + items.length) % items.length];
        // Traverse every control explicitly: browser keyboard preferences can
        // otherwise skip native checkboxes, links and plan selection buttons.
        e.preventDefault();
        next.focus();
        var target = next.matches('.gh-portal-signup-terms input[type="checkbox"]')
            ? next.closest('label') : next;
        if (target) target.scrollIntoView({block: 'nearest', inline: 'nearest'});
    }

    function portalClick(e) {
        if (e.target.closest('.gh-portal-btn-main, [data-test-button="select-tier"]')) validationRequested = true;
    }

    function releasePortal(restoreFocus) {
        if (portalObserver) portalObserver.disconnect();
        if (portalDoc) {
            portalDoc.removeEventListener('keydown', portalKeydown, true);
            portalDoc.removeEventListener('click', portalClick, true);
        }
        portalObserver = null;
        portalDoc = null;
        frame = null;
        validationRequested = false;
        inertElements.forEach(function (el) { el.inert = false; });
        inertElements = [];
        if (restoreFocus && previousFocus && previousFocus.isConnected && !previousFocus.closest('[inert]')) previousFocus.focus();
        if (restoreFocus) previousFocus = null;
    }

    function enhancePortal() {
        var dialog = portalDoc.querySelector('.gh-portal-popup-wrapper');
        if (!dialog) return;
        var heading = dialog.querySelector('h1');
        attr(dialog, 'role', 'dialog');
        attr(dialog, 'aria-modal', 'true');
        if (heading) {
            if (!heading.id) heading.id = 'yoby-portal-heading';
            attr(dialog, 'aria-labelledby', heading.id);
        } else attr(dialog, 'aria-label', text('account', 'YOBY account'));
        attr(portalDoc.documentElement, 'lang', document.documentElement.lang || 'en');
        attr(frame, 'title', text('account', 'YOBY account'));
        attr(frame, 'data-yoby-auth-frame', 'true');

        var title = heading ? heading.textContent.trim() : text('account', 'YOBY account');
        if (portalDoc.title !== title) portalDoc.title = title;
        dialog.querySelectorAll('[tabindex]').forEach(function (el) {
            if (el.tabIndex > 0) el.removeAttribute('tabindex');
        });

        // Remove Portal's maximum-scale=1, which prevents mobile users from zooming.
        var viewport = portalDoc.querySelector('meta[name="viewport"]');
        if (viewport) attr(viewport, 'content', 'width=device-width, initial-scale=1');
        if (!portalDoc.getElementById('yoby-auth-a11y-styles')) {
            var style = portalDoc.createElement('style');
            style.id = 'yoby-auth-a11y-styles';
            style.textContent = PORTAL_CSS;
            portalDoc.head.appendChild(style);
        }
        ['text-lg', 'text-xl', 'spacing', 'focus-strong', 'underline-links', 'reduce-motion', 'readable-font'].forEach(function (suffix) {
            var cls = 'yoby-a11y-' + suffix;
            var enabled = document.documentElement.classList.contains(cls);
            if (portalDoc.documentElement.classList.contains(cls) !== enabled) portalDoc.documentElement.classList.toggle(cls, enabled);
        });

        var authForm = dialog.querySelector('.gh-portal-signup, .gh-portal-content.signin > section');
        if (authForm && heading) {
            attr(authForm, 'role', 'group');
            attr(authForm, 'aria-labelledby', heading.id);
        }
        dialog.querySelectorAll('.gh-portal-btn-main').forEach(function (button) {
            var busy = !!button.querySelector('.gh-portal-loadingicon');
            attr(button, 'aria-busy', busy ? 'true' : 'false');
            if (busy && (!button.hasAttribute('aria-label') || button.hasAttribute('data-yoby-busy-label'))) {
                attr(button, 'aria-label', text('sending', 'Sending…'));
                attr(button, 'data-yoby-busy-label', 'true');
            } else if (!busy && button.hasAttribute('data-yoby-busy-label')) {
                button.removeAttribute('aria-label');
                button.removeAttribute('data-yoby-busy-label');
            }
        });
        dialog.querySelectorAll('.gh-portal-product-card').forEach(function (card) {
            var name = card.querySelector('.gh-portal-product-name');
            var choose = card.querySelector('[data-test-button="select-tier"]');
            if (name && choose && !choose.querySelector('.gh-portal-loadingicon')) {
                attr(choose, 'aria-label', choose.textContent.trim() + ' — ' + name.textContent.trim());
            }
        });
        dialog.querySelectorAll('[data-test-button="switch-monthly"], [data-test-button="switch-yearly"]').forEach(function (button) {
            attr(button, 'aria-pressed', button.classList.contains('active') ? 'true' : 'false');
        });
        var invalid = [];
        var errors = [];
        dialog.querySelectorAll('.gh-portal-input-section:not(.hidden) .gh-portal-input').forEach(function (input) {
            var section = input.closest('.gh-portal-input-section');
            if (input.type === 'hidden' || input.name === 'phonenumber') return;
            if (input.tabIndex > 0) input.removeAttribute('tabindex');
            if (input.name === 'email') attr(input, 'autocomplete', 'email');
            if (input.name === 'name') attr(input, 'autocomplete', 'name');
            if (dialog.querySelector('.gh-portal-popup-container.signin, .gh-portal-popup-container.signup') && /^(email|name)$/.test(input.name)) attr(input, 'aria-required', 'true');
            var message = section.querySelector('.gh-portal-input-labelcontainer p');
            var errorId = 'yoby-' + input.id + '-error';
            var hasError = input.classList.contains('error') && message && message.textContent.trim();
            attr(input, 'aria-invalid', hasError ? 'true' : 'false');
            if (hasError) {
                attr(message, 'id', errorId);
                invalid.push(input);
                errors.push(message.textContent.trim());
            }
            describe(input, errorId, !!hasError);
        });
        var terms = dialog.querySelector('.gh-portal-signup-terms');
        if (terms) {
            var checkbox = terms.querySelector('input[type="checkbox"]');
            if (checkbox) {
                attr(checkbox, 'tabindex', '0');
                attr(checkbox, 'aria-required', checkbox.required ? 'true' : 'false');
                var termsInvalid = terms.classList.contains('gh-portal-error');
                attr(checkbox, 'aria-invalid', termsInvalid ? 'true' : 'false');
                describe(checkbox, 'yoby-portal-errors', termsInvalid);
                if (termsInvalid) {
                    invalid.push(checkbox);
                    errors.push(text('termsRequired', 'Please accept the terms to continue.'));
                }
            }
        }
        dialog.querySelectorAll('[data-test-button="close-popup"]').forEach(function (close) { attr(close, 'aria-label', text('close', 'Close popup')); });
        portalDoc.querySelectorAll('.gh-portal-notification.error').forEach(function (notification) {
            var message = notification.querySelector('p');
            if (message) errors.push(message.textContent.trim());
        });
        var live = portalDoc.querySelector('[data-yoby-auth-live]');
        if (!live) {
            live = portalDoc.createElement('div');
            live.setAttribute('data-yoby-auth-live', '');
            live.id = 'yoby-portal-errors';
            live.setAttribute('role', 'alert');
            live.setAttribute('aria-atomic', 'true');
            dialog.appendChild(live);
        }
        var announcement = errors.join('. ');
        if (live.textContent !== announcement) live.textContent = announcement;
        if (validationRequested && invalid.length) {
            invalid[0].focus();
            validationRequested = false;
        }
    }

    function syncPortal() {
        var nextFrame = document.querySelector('#ghost-portal-root iframe[data-yoby-auth-frame], #ghost-portal-root iframe[title="portal-popup"], #ghost-portal-root iframe.gh-portal-popup-iframe');
        var nextDoc = null;
        try { nextDoc = nextFrame && nextFrame.contentDocument; } catch (e) { /* Cross-origin frames remain controlled by their provider. */ }
        if (nextFrame && !hookedFrames.has(nextFrame)) {
            hookedFrames.add(nextFrame);
            nextFrame.addEventListener('load', schedule);
        }
        if (!nextDoc || !nextDoc.querySelector('.gh-portal-popup-wrapper')) {
            if (portalDoc) releasePortal(true);
            return;
        }
        if (portalDoc !== nextDoc) {
            releasePortal(false);
            frame = nextFrame;
            portalDoc = nextDoc;
            if (!previousFocus) previousFocus = document.activeElement;
            inertElements = Array.prototype.slice.call(document.body.children).filter(function (el) {
                return !el.contains(frame) && !el.inert && !/^(SCRIPT|STYLE|LINK)$/.test(el.tagName);
            });
            inertElements.forEach(function (el) { el.inert = true; });
            portalDoc.addEventListener('keydown', portalKeydown, true);
            portalDoc.addEventListener('click', portalClick, true);
            portalObserver = new MutationObserver(schedule);
            portalObserver.observe(portalDoc.body, {subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'disabled', 'tabindex']});
        }
        enhancePortal();
    }

    function syncMembersForms() {
        document.querySelectorAll('form[data-members-form]').forEach(function (form) {
            var input = form.querySelector('[data-members-email]');
            var button = form.querySelector('button[type="submit"]');
            var error = form.querySelector('[data-members-error]');
            var status = form.querySelector('[data-yoby-members-status]');
            if (!input) return;
            var loading = form.classList.contains('loading');
            var success = form.classList.contains('success');
            var invalid = form.classList.contains('error') && error && !!error.textContent.trim();
            attr(input, 'aria-invalid', invalid ? 'true' : 'false');
            attr(form, 'aria-busy', loading ? 'true' : 'false');
            if (button) {
                attr(button, 'aria-busy', loading ? 'true' : 'false');
                if (success) attr(button, 'aria-label', text('sent', 'Email sent. Check your inbox to continue.'));
            }
            if (status) {
                var message = success ? text('sent', 'Email sent. Check your inbox to continue.') : '';
                if (status.textContent !== message) status.textContent = message;
            }
            if (!hookedForms.has(form)) {
                hookedForms.add(form);
                input.addEventListener('invalid', function () { attr(input, 'aria-invalid', 'true'); });
                input.addEventListener('input', function () {
                    if (!form.classList.contains('error')) attr(input, 'aria-invalid', 'false');
                });
                new MutationObserver(schedule).observe(form, {subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class']});
            }
        });
    }

    function schedule() {
        if (pending !== null) return;
        pending = setTimeout(function () {
            pending = null;
            syncMembersForms();
            syncPortal();
        }, 0);
    }

    function init() {
        if (document.activeElement !== document.body) previousFocus = document.activeElement;
        document.addEventListener('click', function (e) {
            var target = e.target.closest('[data-portal], a[href*="#/portal/"]');
            if (target) previousFocus = target;
        }, true);
        new MutationObserver(schedule).observe(document.body, {childList: true, subtree: true});
        new MutationObserver(schedule).observe(document.documentElement, {attributes: true, attributeFilter: ['class', 'lang']});
        window.addEventListener('hashchange', schedule);
        window.addEventListener('yoby:localechange', schedule);
        schedule();
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
})();
